import { useSelector, useDispatch } from 'react-redux';
import { useState, useEffect } from 'react';
import {
  getProjectItems as getProjectItemsSelector,
  getSelectedProjectItems as getSelectedProjectItemsSelector,
  getOpenedProjectItem as getOpenedProjectItemSelector,
  getProjectItemsLoadingState as getProjectItemsLoadingStateSelector,
  getProjectItemsFilters as getProjectItemsFiltersSelector,
  getQueryArray,
} from 'store/project-items/selectors';
import {
  selectItem as selectItemAction,
  deselectItem as deselectItemAction,
  deselectMany as deselectManyAction,
  resetProjectItems,
  resetProjectItemsActions,
  closeProjectItem,
  getProjectItems as getProjectItemsAction,
  openProjectItem as openItemAction,
  setProjectItemsFilters,
  getProjectItemsDelayed,
} from 'store/project-items/actions';
import Loader from 'library/components/entity-loader';
import ViewMode from 'library/components/view-mode';
import {
  Redirect, Route, Switch, useLocation,
} from 'react-router-dom';
import { FILTER_KEYS } from 'store/project-items/fixtures';
import ROUTES from 'library/constants/routes';
import withNavigationFilters from 'library/hocs/with-navigation-filters';
import * as yup from 'yup';
import CreateProjectModal from 'library/components/modal/input';
import { TYPE as BROWSE_ENTITY_TYPE, TYPE } from 'store/browse-items/fixtures';
import Filters from 'library/components/filters';
import { BatchSelectContainer } from 'library/components/batch-select/styles';
import {
  getProject as getProjectSelector,
  getProjectSelectedData,
  getProjectSelectedDataIds,
} from 'store/project/selectors';
import {
  createProject,
  selectProjectDetailsItem, deselectProjectDetailsItem,
  selectProjectDetailsImage, deselectProjectDetailsImage,
  selectProjectDetailsColor, deselectProjectDetailsColor,
  selectProjectDetailsSet, deselectProjectDetailsSet,
  batchRemoveFromProject, resetProjectDetailsImages,
  resetProjectDetailsItems, resetProjectDetailsSets,
  resetProjectDetailsColors,
} from 'store/project/actions';
import BatchSelect from 'library/components/batch-select';
import NoContent from 'library/components/no-content';
// eslint-disable-next-line import/no-duplicates
import { resetItem } from 'store/items/actions';
import ProjectPage from './project';
import { getBrowseItemsDebaunced } from '../../../store/browse-items/actions';

const {
  CATEGORIES,
  MANUFACTURERS,
  TAGS,
  QUANTITY,
  ITEMS,
  STORAGE,
} = FILTER_KEYS;

const SELECT_ACTIONS_MAP = {
  [BROWSE_ENTITY_TYPE.ITEM]: {
    select: selectProjectDetailsItem,
    deselect: deselectProjectDetailsItem,
    apiType: 'ITEM',
  },
  [BROWSE_ENTITY_TYPE.IMAGE]: {
    select: selectProjectDetailsImage,
    deselect: deselectProjectDetailsImage,
    apiType: 'IMAGE',
  },
  [BROWSE_ENTITY_TYPE.COLOR]: {
    select: selectProjectDetailsColor,
    deselect: deselectProjectDetailsColor,
    apiType: 'COLORS',
  },
  [BROWSE_ENTITY_TYPE.SET]: {
    select: selectProjectDetailsSet,
    deselect: deselectProjectDetailsSet,
    apiType: 'SET',
  },

};

const validation = yup.object().shape({
  name: yup
    .string()
    .required('Project name is required field')
    .min(2, 'Name is too short')
    .max(30, 'Name is too long'),
});

const Project = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const items = useSelector(getProjectItemsSelector);
  const filters = useSelector(getProjectItemsFiltersSelector);
  const loading = useSelector(getProjectItemsLoadingStateSelector);
  const openedItemId = useSelector(getOpenedProjectItemSelector);
  const data = useSelector(getProjectSelector);
  const selectedDataIds = useSelector(getSelectedProjectItemsSelector);
  const {
    selectedProjectImages,
    selectedProjectItems,
    selectedProjectSets,
    selectedProjectColors,
  } = useSelector(getProjectSelectedData);
  const {
    selectedProjectImageIds,
    selectedProjectItemIds,
    selectedProjectSetIds,
    selectedProjectColorIds,
  } = useSelector(getProjectSelectedDataIds);
  const showFilter = !pathname.includes('/project-details');
  const [isCreateModalOpened, setCreateModalOpened] = useState(false);
  const baseQuery = useSelector(getQueryArray);

  useEffect(() => () => {
    dispatch(resetProjectItems());
    dispatch(resetProjectItemsActions());
  }, [data]);

  const handleToggleItemSelected = (id) => {
    if (!selectedDataIds.includes(id)) {
      return dispatch(selectItemAction({ id }));
    }

    return dispatch(deselectItemAction({ id }));
  };
  //
  const handleCloseItem = () => {
    dispatch(closeProjectItem());
  };

  const handleLoadMore = () => {
    dispatch(getProjectItemsAction());
  };

  const handleFilterChange = (values, isDebaunced = false) => {
    if(isDebaunced) {
      dispatch(getProjectItemsDelayed({
        values,
        filtersSnapshot: JSON.parse(JSON.stringify(filters)),
      }));
    } else {
      dispatch(setProjectItemsFilters(values));
      dispatch(getProjectItemsAction());
    }
  };

  const handleQueryParamDelete = ({ type, value }) => {
    const { [type]: updatedField, ...restFilters } = filters;

    dispatch(setProjectItemsFilters({
      ...restFilters,
      ...([TAGS, ITEMS].includes(type) ? { [type]: updatedField.filter((filter) => filter.id !== value) } : {}),
      ...([CATEGORIES, STORAGE].includes(type) ? { [type]: updatedField.filter((itemId) => itemId !== value) } : {}),
      ...(type === MANUFACTURERS ? {
        [type]: updatedField.filter((filter) => {
          return (Array.isArray(value) ? !value.includes(filter) : filter !== value);
        }),
      } : {}),
      ...(type === QUANTITY && { [type]: value || null }),
    }));

    dispatch(deselectManyAction({ ids: selectedDataIds }));
    dispatch(getProjectItemsAction());
  };

  const handleQueryParamsReset = () => {
    dispatch(setProjectItemsFilters());
    dispatch(getProjectItemsAction());

    dispatch(deselectManyAction({ ids: selectedDataIds }));
  };
  const addButtonActions = [
    {
      id: 'project',
      label: 'Create project',
      icon: 'icon-folder',
      action: () => {
        setCreateModalOpened(true);
      },
    },
    // {
    //   id: 'pick',
    //   label: 'Pick item from catalogue',
    //   icon: 'icon-magnifier',
    //   action: () => {
    //     setCreateModalOpened(true);
    //   },
    //   disabled: true
    // },
  ];

  const toggleSelect = ({ type, id }, isSelected) => {
    const { select, deselect } = SELECT_ACTIONS_MAP[type];

    if (isSelected) {
      dispatch(deselect({ id }));
    } else {
      dispatch(select({ id }));
    }
  };

  const handleCreateSetModalClose = () => {
    setCreateModalOpened(false);
  };

  const handleCreateSet = (set) => {
    dispatch(createProject({ data: { ...set, color: '#7468C6' } }));

    setCreateModalOpened(false);
  };

  const handleOpenItem = (id) => {
    dispatch(openItemAction({ id }));
  };

  const handleDeleteFromProject = (type) => {
    const result = {
      id: data.id,
      // ids,
      type: SELECT_ACTIONS_MAP[type].apiType,
      messages: { success: 'Color(s) added successfully' },
      resetProject: true,
    };
    switch (type) {
    case BROWSE_ENTITY_TYPE.SET: {
      result.ids = selectedProjectSetIds;
      result.messages = { success: 'Sets(s) removed successfully' };
      break;
    }
    case BROWSE_ENTITY_TYPE.ITEM: {
      result.ids = selectedProjectItemIds;
      result.messages = { success: 'Item(s) removed successfully' };
      break;
    }
    case BROWSE_ENTITY_TYPE.IMAGE: {
      result.ids = selectedProjectImageIds;
      result.messages = { success: 'Images(s) removed successfully' };
      break;
    }
    case BROWSE_ENTITY_TYPE.COLOR: {
      result.ids = selectedProjectColorIds;
      result.messages = { success: 'Colors(s) removed successfully' };
      break;
    }
    }

    if (result.ids?.length) {
      dispatch(batchRemoveFromProject(result));
    }
  };

  const handleResetItem = () => {
    dispatch(resetItem());
  };

  return (
    <>
      <Switch>
        <Route path={`${ROUTES.PROJECTS}/:mode/project-details/:id`} exact>
          <ProjectPage
            openedItemId={openedItemId}
            handleCloseItem={handleCloseItem}
            handleOpenItem={handleOpenItem}
            handleResetItem={handleResetItem}
            onSelect={toggleSelect}
          />
          <BatchSelectContainer>
            <BatchSelect
              type={BROWSE_ENTITY_TYPE.IMAGE}
              data={selectedProjectImages}
              onDelete={() => handleDeleteFromProject(BROWSE_ENTITY_TYPE.IMAGE)}
              onDeselectAll={() => dispatch(resetProjectDetailsImages())}
              deleteType="project-image"
              disabledActions={['tag']}
              onDeselect={(id) => {
                dispatch(deselectProjectDetailsImage({ id }));
              }}
            />
            <BatchSelect
              type={BROWSE_ENTITY_TYPE.ITEM}
              data={selectedProjectItems}
              onDelete={() => handleDeleteFromProject(BROWSE_ENTITY_TYPE.ITEM)}
              onDeselectAll={() => dispatch(resetProjectDetailsItems())}
              deleteType="project-item"
              disabledActions={['tag', 'drawer', 'manufacturer', 'layers', 'projects', 'set', 'heart']}
              onDeselect={(id) => {
                dispatch(deselectProjectDetailsItem({ id }));
              }}
            />
            <BatchSelect
              type={BROWSE_ENTITY_TYPE.SET}
              data={selectedProjectSets}
              onDelete={() => handleDeleteFromProject(BROWSE_ENTITY_TYPE.SET)}
              onDeselectAll={() => dispatch(resetProjectDetailsSets())}
              deleteType="project-set"
              disabledActions={['plus', 'projects']}
              onDeselect={(id) => {
                dispatch(deselectProjectDetailsSet({ id }));
              }}
            />
            <BatchSelect
              type={BROWSE_ENTITY_TYPE.COLOR}
              data={selectedProjectColors}
              onDelete={() => handleDeleteFromProject(BROWSE_ENTITY_TYPE.COLOR)}
              onDeselectAll={() => dispatch(resetProjectDetailsColors())}
              deleteType="project-color"
              disabledActions={[]}
              onDeselect={(id) => {
                dispatch(deselectProjectDetailsColor({ id }));
              }}
            />
          </BatchSelectContainer>
        </Route>
        <Route path={`${ROUTES.PROJECTS}/:mode`} exact>
          <Filters
            hasAdditionalSortOptions={false}
            filters={filters}
            baseQuery={baseQuery}
            isShown={showFilter}
            showStorage={false}
            onChange={handleFilterChange}
            onDelete={handleQueryParamDelete}
            onReset={handleQueryParamsReset}
            areInactiveCategoriesIncluded
            multiselectProcessDelayMS={2000}
            isContentDisabledWhenOpen
          />
          <ViewMode
            data={items}
            type={TYPE.PROJECT}
            handleToggle={handleToggleItemSelected}
            selectedIds={[]}
            isSelectable={false}
            addButtonActions={addButtonActions}
          />
          {items.length === 0 && !loading ? <NoContent title="Nothing found... We might not have such data in catalogue yet" /> : null}
          <Loader
            isLoading={loading}
            currentAmount={items.length}
            hasMore={filters[FILTER_KEYS.HAS_MORE]}
            onScroll={handleLoadMore}
          />
        </Route>
        <Route path="*">
          <Redirect to={ROUTES.NOT_FOUND} />
        </Route>
      </Switch>
      <CreateProjectModal
        title="Create new project"
        iconClassName="icon-pencil"
        submitText="Create new project"
        cancelText="Cancel"
        isOpened={isCreateModalOpened}
        onClose={handleCreateSetModalClose}
        onSubmit={handleCreateSet}
        overlayClassName="Overlay"
        placeholder="Come up with a name for your project"
        validation={validation}
      />
    </>
  );
};

export default withNavigationFilters(Project);
